import { useQuery, useQueries } from 'react-query';
import { Options } from '@frontend/fetch';
import { LimitedSchemaQueryOptions } from '@frontend/react-query-helpers';
import { SchemaTenCoordinatorV1Service } from '../service';
import { GetLocationRegistrationStatusDetailsIO } from '../types';

export const getGetLocationRegistrationStatusDetailsQueryKey = (req: GetLocationRegistrationStatusDetailsIO['input']) =>
  ['TenCoordinatorV1', 'GetLocationRegistrationStatusDetails', req] as const;
type QueryKey = ReturnType<typeof getGetLocationRegistrationStatusDetailsQueryKey>;

/**
 * A hook that returns a query for the `GetLocationRegistrationStatusDetails` endpoint.
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetLocationRegistrationStatusDetailsQuery = <
  E = unknown,
  D = GetLocationRegistrationStatusDetailsIO['output']
>(
  req: GetLocationRegistrationStatusDetailsIO['input'],
  options?: LimitedSchemaQueryOptions<GetLocationRegistrationStatusDetailsIO, E, D, QueryKey>,
  httpOptions?: Options
) =>
  useQuery<GetLocationRegistrationStatusDetailsIO['output'], E, D, QueryKey>({
    queryKey: getGetLocationRegistrationStatusDetailsQueryKey(req),
    queryFn: () => SchemaTenCoordinatorV1Service.GetLocationRegistrationStatusDetails(req, httpOptions),
    ...options,
  });

/**
 * A hook that returns an array of queries for the `GetLocationRegistrationStatusDetails` endpoint.
 * It takes an array of objects with the following properties:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetLocationRegistrationStatusDetailsQueries = <
  E = unknown,
  D = GetLocationRegistrationStatusDetailsIO['output']
>(
  argsArr: {
    req: GetLocationRegistrationStatusDetailsIO['input'];
    options?: LimitedSchemaQueryOptions<GetLocationRegistrationStatusDetailsIO, E, D, QueryKey>;
    httpOptions?: Options;
  }[]
) =>
  useQueries(
    argsArr.map(({ req, options, httpOptions }) => ({
      queryKey: getGetLocationRegistrationStatusDetailsQueryKey(req),
      queryFn: () => SchemaTenCoordinatorV1Service.GetLocationRegistrationStatusDetails(req, httpOptions),
      ...options,
    }))
  ) as ReturnType<typeof useGetLocationRegistrationStatusDetailsQuery<E, D>>[];
